import React, { useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import { BodyOverride, ModalContainer } from "./styles"

/**
 * A generic, WAI-ARIA compliant modal component. Currently supported features:
 *
 *  - When the Modal is not open, it is not rendered into the DOM.
 *  - When rendered, the Modal is appended to the end of document.body.
 *  - When open, scrolling is frozen on the main document beneath the Modal.
 *  - Pressing the escape key will close the Modal.
 *  - Clicking outside the Modal will close it.
 *
 * https://assortment.io/posts/accessible-modal-component-react-portals-part-1
 */

const Modal = ({ children, onClose, label, labelledBy, ...props }) => {
  const toggleContainer = useRef(null)
  const lastElement = useRef(null)
  const firstElement = useRef(null)

  const focusFirstElement = () => {
    toggleContainer.current.focus()
  }

  const focusLastElement = () => {
    lastElement.current.focus()
  }

  const focusTrapperEvent = (event) => {
    if (!(event.key === "Tab" || event.keyCode === 9)) {
      return
    }

    if (event.shiftKey) {
      // shift + tab
      if (document.activeElement === firstElement.current) {
        focusLastElement()
        event.preventDefault()
      }
    }
    // tab
    else if (document.activeElement === lastElement.current) {
      focusFirstElement()
      event.preventDefault()
    }
  }

  useEffect(() => {
    focusFirstElement()
  }, [])

  // close on ESC key
  useEffect(() => {
    const keyDown = (event) => {
      const key = event.which || event.keyCode
      const symbol = event.key
      if (key === 27 || symbol === "Escape") {
        if (onClose) {
          onClose()
        }
      }
      focusTrapperEvent(event)
    }

    document.addEventListener("keydown", keyDown)
    return () => document.removeEventListener("keydown", keyDown)
  }, [onClose])

  return createPortal(
    <>
      <BodyOverride />
      <ModalContainer ref={toggleContainer} tabIndex={0} onClick={onClose}>
        <span ref={firstElement} tabIndex="0" />
        <aside
          role="dialog"
          aria-modal="true"
          aria-label={label}
          aria-labelledby={labelledBy}
          onClick={(event) => event.stopPropagation()}
          {...props}
        >
          {children}
        </aside>
        <span ref={lastElement} tabIndex="0" />
      </ModalContainer>
    </>,
    document.body,
  )
}

export default Modal
